import { CourseDto, DirectionDto, PublicCourseDto } from 'api/generated';
import { CERT_ACCENTS } from 'components/Modals/BundleBuilderModal/bundleBuilderConfig';

export const normalizeHexColor = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // Theme primary color must be a 6-digit hex so we can append alpha (e.g. #RRGGBBAA)
  const match = trimmed.match(/^#?([0-9a-fA-F]{6})$/);
  if (!match) return undefined;

  return `#${match[1]}`;
};

export const getOptionalTrimmedString = (obj: unknown, key: string): string | undefined => {
  if (!obj || typeof obj !== 'object') return undefined;
  const value = (obj as Record<string, unknown>)[key];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

export type CourseLike =
  | CourseDto
  | PublicCourseDto
  | Partial<CourseDto>
  | Partial<PublicCourseDto>
  | Record<string, unknown>
  | null
  | undefined;

export const getCoursePrimaryColor = (
  course?: CourseLike,
  directionOrFallback?: DirectionDto | string | null,
): string => {
  const directColor = normalizeHexColor(getOptionalTrimmedString(course, 'upsellPrimaryColor'));
  if (directColor) return directColor;

  const slug = getOptionalTrimmedString(course, 'slug')?.toLowerCase();
  if (slug && CERT_ACCENTS[slug]) return CERT_ACCENTS[slug];

  const id = getOptionalTrimmedString(course, 'id');
  if (id) {
    if (id === '0182eefc-e96c-44f0-8768-80b3efb9fd2d') return '#8B5CF6';
    if (id === 'affb8b9d-67f0-4460-99e5-c009cb41d830') return '#F5B700';
    if (id === 'b0663c77-ab87-4c90-a0e8-cd8538fe2169') return '#8B5E3C';
    if (id === '717e4783-53fd-4527-8679-94563b1f9535') return '#38B6FF';
    if (id === '38e739a3-8852-44d8-8da6-423af16d7fb7') return '#22be74';
    if (id === '2b91e4ec-aed2-4542-b0ea-40d49d31bed5') return '#ED5A26';
  }

  const name = (getOptionalTrimmedString(course, 'name') || '').toLowerCase();
  if (name.includes('scs') || name.includes('strength')) return '#8B5CF6';
  if (name.includes('ges') || name.includes('group')) return '#F5B700';
  if (name.includes('ces') || name.includes('corrective')) return '#8B5E3C';
  if (name.includes('cbs') || name.includes('bodybuilding')) return '#38B6FF';
  if (name.includes('cns') || name.includes('nutrition')) return '#22be74';
  if (name.includes('cpt') || name.includes('personal trainer')) return '#ED5A26';

  if (typeof directionOrFallback === 'string' && directionOrFallback) {
    return normalizeHexColor(directionOrFallback) || directionOrFallback;
  }

  if (directionOrFallback && typeof directionOrFallback === 'object') {
    const mainColor = normalizeHexColor(directionOrFallback.mainColorHex);
    if (mainColor) return mainColor;
  }

  return '#DF6438';
};
